import {
  Controller,
  Get,
  Post,
  Put,
  Delete,
  Body,
  Param,
  Query,
  UseGuards,
  Request,
} from '@nestjs/common';
import { InventoryService } from '../services/inventory.service';
import { RecalculateRevenueService } from '../services/recalculate-revenue.service';
import {
  CreateStockMovementDto,
  UpdateStockMovementDto,
  StockMovementQueryDto,
} from '../dto/stock-movement.dto';
import {
  CreateExpenseDto,
  UpdateExpenseDto,
  ExpenseQueryDto,
} from '../dto/expense.dto';
import {
  ProfitReportQueryDto,
  CashFlowQueryDto,
  TopSellingProductsQueryDto,
} from '../dto/report.dto';
import { JwtAuthGuard } from '@/common/guards/jwt-auth.guard';
import { RolesGuard } from '@/common/guards/roles.guard';
import { Roles } from '@/common/guards/roles.decorator';
import { UserRole } from '@/modules/user/schemas/user.schema';
import { MovementReason, MovementType } from '../schemas/stock-movement.schema';
import { ReportPeriod } from '../schemas/profit-report.schema';
import { RevenueCategory, RevenuePaymentMethod } from '../schemas/revenue.schema';

@Controller('inventory')
@UseGuards(JwtAuthGuard, RolesGuard)
export class InventoryController {
  constructor(
    private readonly inventoryService: InventoryService,
    private readonly recalculateRevenueService: RecalculateRevenueService,
  ) {}

  // Stock Movement Endpoints
  @Post('stock-movements')
  @Roles(UserRole.ADMIN )
  async createStockMovement(
    @Body() createDto: CreateStockMovementDto,
    @Request() req: any,
  ) {
    return this.inventoryService.createStockMovement(createDto, req.user?.id);
  }

  @Get('stock-movements')
  @Roles(UserRole.ADMIN  )
  async getStockMovements(@Query() query: StockMovementQueryDto) {
    return this.inventoryService.getStockMovements(query);
  }

  @Get('stock-movements/:id')
  @Roles(UserRole.ADMIN  )
  async getStockMovementById(@Param('id') id: string) {
    return this.inventoryService.getStockMovementById(id);
  }

  @Put('stock-movements/:id')
  @Roles(UserRole.ADMIN )
  async updateStockMovement(
    @Param('id') id: string,
    @Body() updateDto: UpdateStockMovementDto,
  ) {
    return this.inventoryService.updateStockMovement(id, updateDto);
  }

  // Expense Endpoints
  @Post('expenses')
  @Roles(UserRole.ADMIN )
  async createExpense(
    @Body() createDto: CreateExpenseDto,
    @Request() req: any,
  ) {
    return this.inventoryService.createExpense(createDto, req.user?.id);
  }

  @Get('expenses')
  @Roles(UserRole.ADMIN  )
  async getExpenses(@Query() query: ExpenseQueryDto) {
    return this.inventoryService.getExpenses(query);
  }

  @Get('expenses/:id')
  @Roles(UserRole.ADMIN  )
  async getExpenseById(@Param('id') id: string) {
    return this.inventoryService.getExpenseById(id);
  }

  @Put('expenses/:id')
  @Roles(UserRole.ADMIN )
  async updateExpense(
    @Param('id') id: string,
    @Body() updateDto: UpdateExpenseDto,
  ) {
    return this.inventoryService.updateExpense(id, updateDto);
  }

  @Delete('expenses/:id')
  @Roles(UserRole.ADMIN)
  async deleteExpense(@Param('id') id: string) {
    return this.inventoryService.deleteExpense(id);
  }

  // Revenue Endpoints
  @Get('revenues')
  @Roles(UserRole.ADMIN  )
  async getRevenues(@Query() query: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
    category?: string;
    paymentMethod?: string;
  }) {
    const { startDate, endDate, category, paymentMethod, ...rest } = query;
    return this.inventoryService.getRevenues({
      ...rest,
      startDate: startDate ? new Date(startDate) : undefined,
      endDate: endDate ? new Date(endDate) : undefined,
      category: category as RevenueCategory,
      paymentMethod: paymentMethod as RevenuePaymentMethod,
    });
  }

  // Report Endpoints
  @Post('reports/profit')
  @Roles(UserRole.ADMIN )
  async generateProfitReport(@Body() query: ProfitReportQueryDto) {
    return this.inventoryService.generateProfitReport(query);
  }

  @Get('reports/profit')
  @Roles(UserRole.ADMIN  )
  async getProfitReports(
    @Query('period') period?: string,
    @Query('limit') limit?: number,
  ) {
    return this.inventoryService.getProfitReports(period as any, limit);
  }

  @Get('reports/cash-flow')
  @Roles(UserRole.ADMIN  )
  async getCashFlow(@Query() query: CashFlowQueryDto) {
    return this.inventoryService.getCashFlow(query);
  }

  @Get('reports/top-selling-products')
  @Roles(UserRole.ADMIN  )
  async getTopSellingProducts(@Query() query: TopSellingProductsQueryDto) {
    return this.inventoryService.getTopSellingProducts(query);
  }

  // Stock Management Endpoints
  @Get('stock/current-levels')
  @Roles(UserRole.ADMIN  )
  async getCurrentStockLevels() {
    return this.inventoryService.getCurrentStockLevels();
  }

  @Get('stock/low-stock-alerts')
  @Roles(UserRole.ADMIN  )
  async getLowStockAlerts(@Query('threshold') threshold?: number) {
    return this.inventoryService.getLowStockAlerts(threshold);
  }

  // Dashboard Summary Endpoints
  @Get('dashboard/summary')
  @Roles(UserRole.ADMIN  )
  async getDashboardSummary(
    @Query('period') period: 'current_month' | 'all_time' = 'all_time',
    @Query('startDate') startDate?: string,
    @Query('endDate') endDate?: string,
  ) {
    let startOfPeriod: Date;
    let endOfPeriod: Date;

    if (period === 'current_month') {
      const today = new Date();
      startOfPeriod = new Date(today.getFullYear(), today.getMonth(), 1);
      endOfPeriod = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else if (startDate && endDate) {
      startOfPeriod = new Date(startDate);
      endOfPeriod = new Date(endDate);
    } else {
      // Default to all time (last 12 months)
      const today = new Date();
      startOfPeriod = new Date(today.getFullYear() - 1, today.getMonth(), 1);
      endOfPeriod = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    }

    const [monthlyProfit, lowStockProducts, topProducts] = await Promise.all([
      this.inventoryService.generateProfitReport({
        period: ReportPeriod.MONTHLY,
        startDate: startOfPeriod,
        endDate: endOfPeriod,
      }),
      this.inventoryService.getLowStockAlerts(10),
      this.inventoryService.getTopSellingProducts({
        startDate: startOfPeriod,
        endDate: endOfPeriod,
        limit: 5,
      }),
    ]);

    return {
      monthlyProfit: {
        revenue: monthlyProfit.totalRevenue,
        expenses: monthlyProfit.totalExpenses,
        netProfit: monthlyProfit.netProfit,
        profitMargin: monthlyProfit.profitMargin,
      },
      lowStockProducts: lowStockProducts.length,
      topSellingProducts: topProducts,
      period: {
        startDate: startOfPeriod,
        endDate: endOfPeriod,
        type: period
      }
    };
  }

  // Revenue Recalculation Endpoint
  @Post('recalculate-revenue')
  @Roles(UserRole.ADMIN)
  async recalculateRevenue() {
    return this.recalculateRevenueService.recalculateAllRevenues();
  }

  @Post('recalculate-profit-report')
  @Roles(UserRole.ADMIN)
  async recalculateProfitReport(@Body() query: ProfitReportQueryDto) {
    return this.inventoryService.generateProfitReport({
      ...query,
      recalculate: true
    });
  }

  @Delete('clear-profit-reports')
  @Roles(UserRole.ADMIN)
  async clearProfitReports() {
    return this.inventoryService.clearOutdatedProfitReports();
  }

  // Quick Actions
  @Post('quick/add-stock')
  @Roles(UserRole.ADMIN)
  async quickAddStock(
    @Body() data: { productId: string; quantity: number; unitCost: number },
    @Request() req: any,
  ) {
    return this.inventoryService.createStockMovement(
      {
        productId: data.productId,
        movementType: MovementType.IN,
        reason: MovementReason.PURCHASE,
        quantity: data.quantity,
        unitCost: data.unitCost,
        movementDate: new Date(),
      },
      req.user?.id,
    );
  }

  @Post('quick/adjust-stock')
  @Roles(UserRole.ADMIN )
  async quickAdjustStock(
    @Body() data: { productId: string; newStock: number; reason: string },
    @Request() req: any,
  ) {
    const product = await this.inventoryService.getCurrentStockLevels();
    const currentProduct = product.find(p => p._id.toString() === data.productId);
    
    if (!currentProduct) {
      throw new Error('Product not found');
    }

    const difference = data.newStock - currentProduct.stock;
    const movementType = difference > 0 ? 'IN' : 'OUT';
    const quantity = Math.abs(difference);

    return this.inventoryService.createStockMovement(
      {
        productId: data.productId,
        movementType: movementType as any,
        reason: MovementReason.ADJUSTMENT,
        quantity,
        unitCost: currentProduct.factoryPrice || currentProduct.price * 0.6,
        notes: data.reason,
        movementDate: new Date(),
      },
      req.user?.id,
    );
  }
} 